/**
 * CTO run panel (epic 031 task 06) — the Email CTO link test. Answers
 * Chelsie's Sep-4 ask in her words: pick a link, enter a test email address
 * and launch the page/link exactly as it would behave for a real user. Posts
 * `{ mode: 'cto_link', campaignLinkId, testEmail }`; the created run carries
 * the `launchUrl` (task 04) — copy it or open it in a new tab (a real click);
 * an inactive link is allowed but warned about (the run then expires).
 */

import { useState, type FormEvent } from 'react';
import type { CampaignLinkRow, CampaignDetailResponse } from '../../lib/api';
import type { TestRunSummary } from '@leads-platform/shared';
import { Banner } from '../../components/ui/banner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { CopyButton } from '../../components/ui/copy-button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { createRun } from './test-runs.api';

interface CtoRunPanelProps {
  campaign: CampaignDetailResponse;
  links: CampaignLinkRow[];
  linksLoading: boolean;
  /** Called with the created run — the screen refreshes the list and selects it. */
  onCreated: (run: TestRunSummary) => void;
}

export function CtoRunPanel({ campaign, links, linksLoading, onCreated }: CtoRunPanelProps) {
  const [linkId, setLinkId] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [created, setCreated] = useState<TestRunSummary | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!linkId) return;
    setSubmitting(true);
    setError(null);
    try {
      const run = await createRun(campaign.id, {
        mode: 'cto_link',
        campaignLinkId: linkId,
        testEmail: testEmail.trim(),
      });
      setCreated(run);
      onCreated(run);
    } catch (err) {
      setCreated(null);
      setError(
        err instanceof Error && err.message ? err.message : 'Creating the test link failed.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const inactiveWarning = created?.warnings.includes('link_inactive') ?? false;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Create a CTO test link</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="max-w-2xl text-sm text-muted">
          Enter a test email address and launch the link exactly as it would behave for a real user.
          The address is marked as a test — it is never subscribed.
        </p>
        <form onSubmit={handleSubmit} className="max-w-xl space-y-4">
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="space-y-2">
            <Label htmlFor="cto-link-select">Link</Label>
            <Select
              id="cto-link-select"
              value={linkId}
              onChange={(e) => setLinkId(e.target.value)}
              disabled={linksLoading}
            >
              <option value="">{linksLoading ? 'Loading links…' : 'Pick a link…'}</option>
              {links.map((link) => (
                <option key={link.id} value={link.id}>
                  {`${link.siteLabel} · ${link.promoSlug}`}
                  {link.status === 'inactive' ? ' (inactive)' : ''}
                </option>
              ))}
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="cto-test-email">Test email</Label>
            <Input
              id="cto-test-email"
              type="email"
              required
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
            />
            <p className="text-xs text-muted-foreground">
              Use a real address you can open — the launch is a live click.
            </p>
          </div>
          <Button type="submit" disabled={submitting || !linkId}>
            {submitting ? 'Creating…' : 'Create test link'}
          </Button>
        </form>

        {created && (
          <div className="max-w-xl space-y-3 rounded-lg border border-line bg-paper/60 p-4">
            {inactiveWarning && (
              <Banner tone="warn" title="Inactive link.">
                This link is currently inactive — the test run expires after 30 minutes without a
                click.
              </Banner>
            )}
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-faint">
                Test link
              </div>
              <div className="flex items-start gap-2">
                <span className="min-w-0 flex-1 break-all font-mono text-xs text-ink">
                  {created.launchUrl}
                </span>
                <CopyButton value={created.launchUrl} label="Copy" title="Copy the test link" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                onClick={() =>
                  created.launchUrl &&
                  window.open(created.launchUrl, '_blank', 'noopener,noreferrer')
                }
              >
                Open in new tab
              </Button>
              <span className="text-xs text-muted">
                Opening this link captures a test lead exactly like a real click (the email is
                marked as a test).
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
